import * as React from "react";

import { HeadingLarge, HeadingSmall, LabelSmall } from "kanopi-components/typography";
import {
  useTrackCheckoutStepCompleted,
  useTrackCheckoutStepViewed,
  useTrackOrderCompleted,
  useTrackPaymentInfoEntered,
} from "../hooks/useTracking";

import { Block } from "kanopi-components/Block";
import { Button } from "kanopi-components/Button";
import { Kontainer } from "kanopi-components/Layout/Kontainer";
import { PaymentForm } from "./PaymentForm/PaymentForm";
import { Placeholder } from "kanopi-components/Placeholder";
import { QuotePrice } from "./QuotePrice";
import { QuoteState } from "kw/api/zeus";
import { SectionHeader } from "kw/components/SectionHeader/SectionHeader";
import { Sektion } from "kanopi-components/Layout/Sektion";
import { toaster } from "kanopi-components/ToastContainer";
import { useAPI } from "kw/api/useAPI";
import { useContactUs } from "kw/components/ContactUs/useContactUs";
import { useNavigate } from "@reach/router";
import { usePriceRiskProfile } from "../hooks/usePriceRiskProfile";
import { useStyletron } from "kanopi-components/theme/useStyletron";

type TStatelessPayment = {
  riskProfile?: TRiskProfile;
  loading?: boolean;
  quote?: TQuote;
};

const StatelessPayment: React.FC<TStatelessPayment> = ({
  riskProfile = {} as TRiskProfile,
  quote,
  loading,
}) => {
  const navigate = useNavigate();
  const trackOrderCompleted = useTrackOrderCompleted();
  const trackPaymentInfoEntered = useTrackPaymentInfoEntered();
  const trackStepViewed = useTrackCheckoutStepViewed({ flowType: "Payment" });
  const trackStepCompleted = useTrackCheckoutStepCompleted({ flowType: "Payment" });
  const [, , { priceLoading, pricingFailed }] = usePriceRiskProfile();
  const [, theme] = useStyletron();
  const { contactUs } = useContactUs();
  const { mutation } = useAPI();

  function confirmQuote(riskProfileId) {
    mutation(
      {
        confirmQuote: [
          {
            riskProfileId: riskProfileId,
          },
          {
            paymentSuccess: true,
            bindSuccess: true,
            errorMessage: true,
          },
        ],
      },
      (response: { confirmQuote: { paymentSuccess: boolean } }) => {
        // An unsuccessful response will generate an error handled by the API hook
        if (response.confirmQuote.paymentSuccess) {
          toaster({
            kind: "POSITIVE",
            content: "Quote has been confirmed.",
            key: "decline-referred-quote-success-toast",
            overrides: {
              Body: {
                style: {
                  marginTop: "65px",
                },
              },
            },
          });
        }
      }
    );
  }

  function handleSubmit(skipPayment = false) {
    if (skipPayment) {
      confirmQuote(riskProfile?.id);
    }
    // Replace history so that back navigation doesn't work
    navigate(`/policy/${riskProfile?.id}/confirmed/`, { replace: true });
    trackStepCompleted({ riskProfileId: riskProfile.id, step: 3, quote });
    trackPaymentInfoEntered({ riskProfileId: riskProfile.id, step: 3, quote });
    trackOrderCompleted({ riskProfile, quote });
  }

  React.useEffect(() => {
    if (!loading) trackStepViewed({ riskProfileId: riskProfile.id, step: 3, quote });
  }, [loading]);

  React.useEffect(() => {
    if (riskProfile?.quotes?.[0]?.product?.skipPayments) {
      handleSubmit(true);
    }
  }, [riskProfile]);

  const excludedProducts = [];
  riskProfile?.referredProducts?.map((product) => {
    excludedProducts.push({
      key: product?.key,
      name: product?.name,
      reason: "Referred",
      details:
        "This policy has been referred to one of our agents for assessment. An agent " +
        "will contact you shortly to discuss this policy. Alternatively, you can " +
        `contact us at ${contactUs?.phoneDisplay}.`,
    });
  });
  riskProfile?.knockoutProducts?.map((product) => {
    excludedProducts.push({
      key: product?.key,
      name: product?.name,
      reason: "Unable to complete",
      details:
        "Based on your previous answers, we are unable to offer you this policy.",
    });
  });

  return (
    <Placeholder loading={loading}>
      <Block>
        <SectionHeader title="Insurance" />
        <HeadingLarge marginTop="scale600" marginBottom={0} center>
          Payment
        </HeadingLarge>

        <Sektion compact={true}>
          {riskProfile?.enabledProducts?.map((enabledProduct, index) => {
            const quote = riskProfile?.quotes?.find(
              (q) =>
                q?.product?.key === enabledProduct?.key &&
                [QuoteState.QUOTED, QuoteState.ACCEPTED].includes(q?.state)
            );
            return (
              <Block
                key={index}
                maxWidth="490px"
                display="flex"
                flexDirection="column"
                justifyContent="space-around"
                alignItems="center"
                flex={1}
                margin="0 auto"
                marginTop={index > 0 ? theme.sizing.scale1000 : 0}
                paddingTop={theme.sizing.scale500}
                backgroundColor={theme.colors.backgroundSecondary}
                borderRadius={theme.borders.radius200}
              >
                <HeadingSmall center={true} margin={0}>
                  {quote?.product?.name}
                </HeadingSmall>
                <LabelSmall margin={theme.sizing.scale200}>
                  <QuotePrice
                    inline={true}
                    periodicPrice={quote?.periodicPrice}
                    paymentFrequency={quote?.paymentFrequency}
                    loading={priceLoading}
                    pricingFailed={pricingFailed}
                    idPrefix={quote?.product?.key}
                  />
                </LabelSmall>
              </Block>
            );
          })}

          {excludedProducts.map((product, index) => (
            <Block
              key={index}
              maxWidth="490px"
              minHeight="88px"
              display="flex"
              flexDirection="column"
              justifyContent="space-around"
              alignItems="center"
              flex={1}
              margin="0 auto"
              marginTop={theme.sizing.scale1000}
              paddingTop={theme.sizing.scale500}
              paddingBottom={theme.sizing.scale500}
              backgroundColor={theme.colors.backgroundSecondary}
            >
              <HeadingSmall margin={0}>{product?.name}</HeadingSmall>
              <LabelSmall
                marginLeft={theme.sizing.scale500}
                marginRight={theme.sizing.scale500}
                center
              >
                {product?.details}
              </LabelSmall>
            </Block>
          ))}
          <Kontainer>
            <Block maxWidth={[null, null, "460px"]} margin="0 auto">
              <PaymentForm
                riskProfile={riskProfile}
                paymentGateway={riskProfile?.quotes?.[0].product?.paymentGateway}
                onSubmit={handleSubmit}
              />
            </Block>
          </Kontainer>
        </Sektion>
      </Block>
    </Placeholder>
  );
};

export { StatelessPayment };
